import {
	Box,
	Button,
	Callout,
	Dialog,
	Flex,
	Heading,
	ScrollArea,
} from '@radix-ui/themes'
import { Link } from '@remix-run/react'
import cn from 'clsx'
import * as React from 'react'
import { RxInfoCircled } from 'react-icons/rx'

import { appConfig } from '#app/app-config.ts'
import { CheckoutLink } from '#app/components/common/checkout-link.tsx'
import { PackageOpen } from '#app/components/icon'
import { CloseButton } from '#app/components/ui/close-button/close-button.tsx'
import { type GetOrderItem } from '#app/models/ecommerce-providers/sylius/shop/fetch/order-item/get-order-item'
import { type ProductNormalized } from '#app/models/ecommerce-providers/sylius/shop/interface/product'
import { type ProductOptionValueNormalized } from '#app/models/ecommerce-providers/sylius/shop/interface/product-option-value'
import { type ProductVariantNormalized } from '#app/models/ecommerce-providers/sylius/shop/interface/product-variant'
import { type Locale } from '#app/models/locale'
import { CART_TEST_ID } from '#app/routes/($lang).cart/route-config'
import { CartListItem } from '#app/routes/_layout/cart-list-item'
import { type App } from '#app/routes/_layout/component.route.tsx'
import { useCart } from '#app/utils/client/cart-context.tsx'
import { testId } from '#app/utils/client/test-id.ts'

enum TEST_IDS {
	cartPopover = 'cart-popover',
	items = 'cart-items',
}

/* -------------------------------------------------------------------------------------------------
 * CartPopover
 * -----------------------------------------------------------------------------------------------*/

type CartPopoverProps = {
	app: App
	locale: Locale
	setApp: React.Dispatch<React.SetStateAction<App>>
	isOptimistic?: boolean
}

const CartPopover: React.FC<CartPopoverProps> = (props) => {
	const { locale, setApp } = props

	const { cartState, cartT } = useCart()

	return (
		<ScrollArea
			type="auto"
			scrollbars="vertical"
			className="h-dvh bg-gray-1 px-4"
			{...testId(TEST_IDS.cartPopover)}
		>
			<Flex
				direction="column"
				top="0"
				pt="5"
				pr="2"
				className="stickybg-gray-1 z-10"
			>
				<Flex align="center" justify="between" mb="2">
					<Link
						to={`/${locale}/products`}
						prefetch="viewport"
						onClick={() =>
							setApp((prev) => ({
								...prev,
								displaySidebar: false,
							}))
						}
					>
						<Button size="1" variant="ghost">
							{cartT('noItems.button.products')}
						</Button>
					</Link>
					<Dialog.Close>
						<CloseButton
							onClose={() =>
								setApp((prev) => ({
									...prev,
									displaySidebar: false,
								}))
							}
						/>
					</Dialog.Close>
				</Flex>
				<Flex asChild direction="column" gapY="5">
					<header>
						<Callout.Root color="green" size="1">
							<Callout.Icon>
								<RxInfoCircled size="1.2em" />
							</Callout.Icon>
							<Callout.Text weight="medium">
								{cartT('header.common.info-$val', {
									val: appConfig.free_shipping / 100,
									currency: appConfig.currency,
									locale: locale,
								})}
							</Callout.Text>
						</Callout.Root>
						{!!cartState.itemsCount && (
							<Link
								to={`/${locale}/cart`}
								prefetch="viewport"
								onClick={() =>
									setApp((prev) => ({
										...prev,
										displaySidebar: false,
									}))
								}
							>
								<Dialog.Title
									color="gray"
									size="4"
									data-testid={CART_TEST_ID.cartCount}
								>
									{cartT('items.common.cartTitle', {
										count: cartState.itemsCount,
									})}
								</Dialog.Title>
							</Link>
						)}
					</header>
				</Flex>
			</Flex>
			{!cartState.itemsCount && (
				<Flex
					data-testid={CART_TEST_ID.cartEmpty}
					direction="column"
					justify="center"
					align="center"
					gapY="5"
					pb="5"
					flexGrow="1"
				>
					<Flex
						justify="center"
						align="center"
						className="h-24 w-24 rounded-full border border-dashed border-accent-8 bg-gray-4"
					>
						<PackageOpen
							className="fill-transparent stroke-accent-8 stroke-12"
							width={60}
							height={60}
						/>
					</Flex>
					<Heading as="h2" color="gray">
						{cartT('noItems.common.title')}
					</Heading>
					<Link
						to={`/${locale}/products`}
						prefetch="viewport"
						onClick={() =>
							setApp((prev) => ({
								...prev,
								displaySidebar: false,
							}))
						}
					>
						<Button size="3" variant="surface">
							{cartT('noItems.button.products')}
						</Button>
					</Link>
				</Flex>
			)}
			{!!cartState.itemsCount && (
				<Flex
					data-testid={CART_TEST_ID.cartItems}
					direction="column"
					flexGrow="1"
				>
					<Box flexGrow="1" px={{ initial: '3', lg: '4' }}>
						<ul className="divide-y divide-gray-6" {...testId(TEST_IDS.items)}>
							{cartState.order.items?.map((item: GetOrderItem) => (
								<CartListItem
									key={(item.variant as ProductVariantNormalized).code}
									variant={item.variant as ProductVariantNormalized}
									locale={locale}
									optionValues={
										(item.variant as ProductVariantNormalized).optionValues as
											| ProductOptionValueNormalized[]
											| undefined
									}
									image={
										(item.variant as ProductVariantNormalized).images?.[0] ||
										(
											(item.variant as ProductVariantNormalized)
												.product as ProductNormalized
										).images![0]
									}
									name={item.productName!}
									productSlug={
										(
											(item.variant as ProductVariantNormalized)
												.product as ProductNormalized
										).slug!
									}
									orderItemId={item.id!}
									setApp={setApp}
								/>
							))}
						</ul>
					</Box>
					<CheckoutLink
						locale={locale}
						setApp={setApp}
						className={cn('sticky bottom-0 z-10 shrink-0 bg-gray-1 pb-5', {
							hidden: !cartState.itemsCount,
							'animate-pulse': !!cartState.pending.length,
						})}
						optimisticItemsTotal={cartState.order.itemsTotal!}
						optimisticShippingTotal={cartState.order.shippingTotal!}
						optimisticTaxTotal={cartState.order.taxTotal!}
						optimisticTotal={cartState.order.total!}
					/>
				</Flex>
			)}
		</ScrollArea>
	)
}
CartPopover.displayName = 'CartPopover'

/* -----------------------------------------------------------------------------------------------*/

export { CartPopover, TEST_IDS as CART_POPOVER_TEST_IDS }
